import { VariantProps, cva } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '@/lib/utils';
const tableVariants = cva('text-xs w-full', {
  variants: {
    variant: {
      default: 'bg-white',
      transparent: 'bg-transparent'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const Table = React.forwardRef<HTMLTableElement, React.HTMLAttributes<HTMLTableElement> & VariantProps<typeof tableVariants>>(({
  className,
  variant,
  ...props
}, ref) => <table ref={ref} className={cn(tableVariants({
  variant
}), className)} {...props} />);
Table.displayName = 'Table';
const tableHeaderVariants = cva('sticky top-0 z-10 [&_tr]:border-b shadow-sm', {
  variants: {
    variant: {
      default: 'bg-white',
      transparent: 'bg-background'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement> & VariantProps<typeof tableVariants>>(({
  className,
  variant,
  ...props
}, ref) => <thead ref={ref} className={cn(tableHeaderVariants({
  variant
}), className)} {...props}>
    {props.children}
  </thead>);
TableHeader.displayName = 'TableHeader';
const tableBodyVariants = cva('text-xs [&_tr:last-child]:border-0 font-mono', {
  variants: {
    variant: {
      default: '',
      transparent: ''
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement> & VariantProps<typeof tableVariants>>(({
  className,
  variant,
  ...props
}, ref) => <tbody ref={ref} className={cn(tableBodyVariants({
  variant
}), className)} {...props} />);
TableBody.displayName = 'TableBody';
const tableFooterVariants = cva('border-t bg-zinc-100/50 font-medium [&>tr]:last:border-b-0', {
  variants: {
    variant: {
      default: '',
      transparent: ''
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement> & VariantProps<typeof tableVariants>>(({
  className,
  variant,
  ...props
}, ref) => <tfoot ref={ref} className={cn(tableFooterVariants({
  variant
}), className)} {...props} />);
TableFooter.displayName = 'TableFooter';
const tableRowVariants = cva('h-12 border-b transition-colors hover:bg-zinc-100/50 data-[state=selected]:bg-zinc-100', {
  variants: {
    variant: {
      default: '',
      transparent: ''
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement> & VariantProps<typeof tableVariants>>(({
  className,
  variant,
  ...props
}, ref) => <tr ref={ref} className={cn(tableRowVariants({
  variant
}), className)} {...props} />);
TableRow.displayName = 'TableRow';
const tableHeadVariants = cva('h-12 px-4 text-left align-middle font-heading font-medium tracking-wide text-xs whitespace-nowrap [&:has([role=checkbox])]:pr-0 ', {
  variants: {
    variant: {
      default: '',
      transparent: ''
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement> & VariantProps<typeof tableVariants>>(({
  className,
  variant,
  ...props
}, ref) => <th ref={ref} className={cn(tableHeadVariants({
  variant
}), className)} {...props} />);
TableHead.displayName = 'TableHead';
const tableCellVariants = cva('align-middle [&:has([role=checkbox])]:pr-0', {
  variants: {
    variant: {
      default: '',
      transparent: ''
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement> & VariantProps<typeof tableVariants>>(({
  className,
  variant = 'default',
  ...props
}, ref) => <td ref={ref} className={cn(tableCellVariants({
  variant
}), className)} {...props}>
    <div className="overflow-hidden text-ellipsis whitespace-nowrap max-w-[400px]">
      {props.children}
    </div>
  </td>);
TableCell.displayName = 'TableCell';
const tableCaptionVariants = cva('mt-4 text-sm text-zinc-500', {
  variants: {
    variant: {
      default: '',
      transparent: ''
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement> & VariantProps<typeof tableVariants>>(({
  className,
  variant,
  ...props
}, ref) => <caption ref={ref} className={cn(tableCaptionVariants({
  variant
}), className)} {...props} />);
TableCaption.displayName = 'TableCaption';
export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };